import React, { useEffect, useMemo, useState } from 'react';
import { Table, Select, Input, Checkbox, Button, Tag, Space, Typography, Alert, message } from 'antd';
import PageShell from '../components/page/PageShell';
import PageHeader from '../components/page/PageHeader';
import PageState from '../components/page/PageState';
import PageTabs from '../components/page/PageTabs';
import { ReloadOutlined } from '@ant-design/icons';
import { useApp } from '../context/AppContext';
import { useSupportApi } from '../hooks/useSupportApi';
import { useResource } from '../hooks/useResource';
import TicketDetailDrawer from '../components/support/TicketDetailDrawer';
import {
    SUPPORT_CATEGORIES,
    SUPPORT_PRIORITIES,
    SUPPORT_STATUSES,
    PRIORITY_COLOR,
    STATUS_COLOR,
    humanizeEnum,
    formatDdMmYyyy,
    formatDdMmYyyyTime,
    shortId,
    resolutionSlaState,
    filterTicketsForDisplay,
} from '../utils/supportApi';
import { displayEmail } from '../utils/displayEmail';

const { Title, Text } = Typography;

const PAGE_SIZE = 50;

function RoutingRulesTab({ api }) {
    const rules = useResource(() => api.listRoutingRules(), [api]);
    const [category, setCategory] = useState(undefined);
    const [team, setTeam] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            await api.upsertRoutingRule({ category, assignToTeam: team.trim(), isActive });
            message.success('Routing rule saved.');
            setCategory(undefined);
            setTeam('');
            setIsActive(true);
            rules.reload();
        } catch (err) {
            message.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    if (rules.status === 'loading' || rules.status === 'idle') return <PageState status="loading" />;
    if (rules.status === 'error') {
        return (
            <Alert
                type="error"
                showIcon
                message="Could not load routing rules"
                description={rules.error.message}
                action={<Button onClick={rules.reload}>Retry</Button>}
            />
        );
    }

    return (
        <div>
            <Text type="secondary">
                A rule names the support team a new ticket of that category is routed to. Routing is recorded in the audit log; it does not assign an individual.
            </Text>
            <div style={{ margin: '16px 0' }}>
                <Space wrap>
                    <Select
                        aria-label="Rule category"
                        style={{ width: 200 }}
                        placeholder="Category"
                        value={category}
                        onChange={setCategory}
                        options={SUPPORT_CATEGORIES.map((c) => ({ value: c, label: humanizeEnum(c) }))}
                    />
                    <Input aria-label="Team" style={{ width: 240 }} placeholder="Team name" value={team} onChange={(e) => setTeam(e.target.value)} />
                    <Checkbox checked={isActive} onChange={(e) => setIsActive(e.target.checked)}>Active</Checkbox>
                    <Button type="primary" loading={saving} disabled={!category || !team.trim()} onClick={save}>
                        Save rule
                    </Button>
                </Space>
            </div>
            {rules.data.length === 0 ? (
                <PageState status="empty" message="No routing rules configured. New tickets are left unrouted until you add one." />
            ) : (
                <Table
                    rowKey={(r) => r.id}
                    dataSource={rules.data}
                    pagination={false}
                    columns={[
                        { title: 'Category', dataIndex: 'category', render: (v) => humanizeEnum(v) },
                        { title: 'Team', dataIndex: 'assignToTeam' },
                        { title: 'Active', dataIndex: 'isActive', render: (v) => (v ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>) },
                    ]}
                />
            )}
        </div>
    );
}

/**
 * Prolense backoffice ticket console — support staff (platform_admin / support_agent).
 * Cites: .claude/design/backoffice-ticketing-pathkey-sqa.md, Sub-workstream A ("Ticket queue —
 * all tenants; filter by status / priority / assigned / breach-imminent").
 *
 * Persona gate: SupportPage mounts this only when the server's `/me` says the caller is support
 * staff (`effectiveStaffRole`); the real control is still the server — `PATCH /support/tickets/:id`,
 * `/admin/support/staff` and `/admin/support/routing-rules` are route-gated to
 * platform_admin/support_agent. The list is severity-sorted and paginated BY THE SERVER; status,
 * assignee, tenant and breached are server filters. Priority / category / search narrow only the
 * page already fetched, for convenience (never a security control).
 */
const SupportConsolePage = ({ myUserId }) => {
    const api = useSupportApi();
    const { projects } = useApp();
    const [status, setStatus] = useState(undefined);
    const [mineOnly, setMineOnly] = useState(false);
    const [breachedOnly, setBreachedOnly] = useState(false);
    const [tenantId, setTenantId] = useState(undefined);
    const [page, setPage] = useState(1);
    const [priority, setPriority] = useState(undefined);
    const [category, setCategory] = useState(undefined);
    const [search, setSearch] = useState('');
    const [selectedId, setSelectedId] = useState(null);
    const [overrides, setOverrides] = useState({});
    const [seenTenants, setSeenTenants] = useState({});

    const staff = useResource(() => api.listSupportStaff(), [api]);

    // Project labels come from the already-loaded portfolio (a platform_admin's /projects returns
    // every project, uncapped) — no capped /companies lookup.
    const projectLabels = useMemo(() => {
        const labels = {};
        (projects || []).forEach((p) => {
            labels[p.id] = `${p.companyName || p.name} · ${p.name}`;
        });
        return labels;
    }, [projects]);

    const pickPage = (fn) => (v) => {
        fn(v);
        setPage(1);
    };

    const tickets = useResource(
        () =>
            api.listTickets({
                status,
                assignedToUserId: mineOnly ? myUserId : undefined,
                tenantId,
                breached: breachedOnly ? 'true' : undefined,
                page,
                pageSize: PAGE_SIZE,
            }),
        [api, status, mineOnly, myUserId, tenantId, breachedOnly, page],
    );

    // A fresh server page supersedes locally patched copies; remember tenants for the filter.
    useEffect(() => {
        setOverrides({});
        if (tickets.status === 'ready') {
            setSeenTenants((prev) => {
                const next = { ...prev };
                tickets.data.tickets.forEach((t) => {
                    if (t.tenantId) next[t.tenantId] = t.tenantName || `Tenant ${shortId(t.tenantId)}`;
                });
                return next;
            });
        }
    }, [tickets.data]);

    const pageRows = tickets.status === 'ready' ? tickets.data.tickets.map((t) => overrides[t.id] || t) : [];
    // Server order (severity rank) is preserved; only narrowing happens here.
    const rows = filterTicketsForDisplay(pageRows, { priority, category, search, projectLabels });
    const selected = selectedId ? pageRows.find((t) => t.id === selectedId) || null : null;

    const staffName = (id) => {
        if (!id) return 'Unassigned';
        if (id === myUserId) return 'You';
        const u = staff.status === 'ready' ? staff.data.find((s) => s.id === id) : null;
        return u ? u.name : `User ${shortId(id)}`;
    };

    const columns = [
        {
            title: 'Ticket',
            dataIndex: 'title',
            render: (v, t) => (
                <div>
                    <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => setSelectedId(t.id)}>
                        {v}
                    </Button>
                    <div><Text type="secondary">{shortId(t.id)}</Text></div>
                </div>
            ),
        },
        {
            title: 'Tenant / project',
            render: (_, t) => (
                <div>
                    {t.tenantId ? (
                        <Button
                            type="link"
                            aria-label={`Filter to tenant ${t.tenantName || shortId(t.tenantId)}`}
                            style={{ padding: 0, height: 'auto' }}
                            onClick={() => pickPage(setTenantId)(t.tenantId)}
                        >
                            {t.tenantName || `Tenant ${shortId(t.tenantId)}`}
                        </Button>
                    ) : (
                        <Text>Platform-level</Text>
                    )}
                    <div>
                        <Text type="secondary">{t.projectId ? projectLabels[t.projectId] || `Project ${shortId(t.projectId)}` : 'No project'}</Text>
                    </div>
                </div>
            ),
        },
        {
            title: 'Reporter',
            render: (_, t) => (
                <div>
                    <Text>{t.reporterName || `User ${shortId(t.reporterUserId)}`}</Text>
                    {t.reporterEmail && <div><Text type="secondary">{displayEmail(t.reporterEmail)}</Text></div>}
                </div>
            ),
        },
        { title: 'Category', dataIndex: 'category', render: (v) => humanizeEnum(v) },
        { title: 'Priority', dataIndex: 'priority', render: (v) => <Tag color={PRIORITY_COLOR[v]}>{humanizeEnum(v)}</Tag> },
        { title: 'Status', dataIndex: 'status', render: (v) => <Tag color={STATUS_COLOR[v]}>{humanizeEnum(v)}</Tag> },
        { title: 'Assignee', render: (_, t) => staffName(t.assignedToUserId) },
        {
            title: 'Resolution due',
            render: (_, t) => {
                const state = resolutionSlaState(t);
                return (
                    <div>
                        <Text>{formatDdMmYyyy(t.slaResolutionDueAt)}</Text>
                        {state === 'breached' && <div><Tag color="red">Breached</Tag></div>}
                        {state === 'overdue' && <div><Tag color="volcano">Overdue</Tag></div>}
                    </div>
                );
            },
        },
        { title: 'Raised', dataIndex: 'createdAt', render: (v) => formatDdMmYyyyTime(v) },
    ];

    const renderTickets = () => {
        if (tickets.status === 'loading' || tickets.status === 'idle') return <PageState status="loading" />;
        if (tickets.status === 'error') {
            return (
                <Alert
                    type="error"
                    showIcon
                    message="Could not load tickets"
                    description={tickets.error.message}
                    action={<Button onClick={tickets.reload}>Retry</Button>}
                />
            );
        }
        if (tickets.data.total === 0) {
            return <PageState status="empty" message="No tickets match the server filters. Clear status, tenant, assignee or breached to see the whole queue." />;
        }
        return (
            <>
                <div style={{ marginBottom: 8 }}>
                    <Text type="secondary">
                        {tickets.data.total} ticket{tickets.data.total === 1 ? '' : 's'} match, most severe first. Priority, category and search narrow this page only.
                    </Text>
                </div>
                {rows.length === 0 ? (
                    <PageState status="empty" message="No tickets on this page match the priority, category or search filter." />
                ) : (
                    <Table
                        rowKey={(r) => r.id}
                        dataSource={rows}
                        columns={columns}
                        sticky
                        pagination={{
                            current: tickets.data.page,
                            pageSize: tickets.data.pageSize,
                            total: tickets.data.total,
                            showSizeChanger: false,
                            onChange: (p) => setPage(p),
                        }}
                    />
                )}
            </>
        );
    };

    const tenantOptions = Object.entries(seenTenants).map(([id, name]) => ({ value: id, label: name }));

    const ticketsTab = (
        <div>
            <Space wrap style={{ marginBottom: 16 }}>
                <Select
                    aria-label="Status filter"
                    allowClear
                    style={{ width: 180 }}
                    placeholder="Status"
                    value={status}
                    onChange={pickPage(setStatus)}
                    options={SUPPORT_STATUSES.map((s) => ({ value: s, label: humanizeEnum(s) }))}
                />
                <Select
                    aria-label="Tenant filter"
                    allowClear
                    style={{ width: 220 }}
                    placeholder="Tenant"
                    value={tenantId}
                    onChange={pickPage(setTenantId)}
                    options={tenantOptions}
                />
                <Checkbox checked={breachedOnly} onChange={(e) => pickPage(setBreachedOnly)(e.target.checked)}>
                    Breached or overdue
                </Checkbox>
                <Checkbox checked={mineOnly} disabled={!myUserId} onChange={(e) => pickPage(setMineOnly)(e.target.checked)}>
                    Assigned to me
                </Checkbox>
                <Select
                    aria-label="Priority filter"
                    allowClear
                    style={{ width: 150 }}
                    placeholder="Priority (this page)"
                    value={priority}
                    onChange={setPriority}
                    options={SUPPORT_PRIORITIES.map((p) => ({ value: p, label: humanizeEnum(p) }))}
                />
                <Select
                    aria-label="Category filter"
                    allowClear
                    style={{ width: 200 }}
                    placeholder="Category (this page)"
                    value={category}
                    onChange={setCategory}
                    options={SUPPORT_CATEGORIES.map((c) => ({ value: c, label: humanizeEnum(c) }))}
                />
                <Input
                    aria-label="Search tickets"
                    allowClear
                    style={{ width: 260 }}
                    placeholder="Search this page: title, tenant, reporter, project"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <Button icon={<ReloadOutlined />} onClick={tickets.reload}>Refresh</Button>
            </Space>
            {staff.status === 'error' && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="Could not load the support staff list"
                    description={`${staff.error.message} Assignee names and the assignee picker are unavailable until it loads.`}
                    action={<Button onClick={staff.reload}>Retry</Button>}
                />
            )}
            {renderTickets()}
        </div>
    );

    return (
        <PageShell>
            <PageHeader
                title="Support console"
                subtitle="Tickets raised by every tenant. Every change you make is audit-logged against the ticket's tenant, and opening another tenant's ticket (including its attachments) is logged as a cross-tenant view on that tenant's audit trail."
            />
            <PageTabs
                items={[
                    { key: 'tickets', label: 'Tickets', children: ticketsTab },
                    { key: 'routing', label: 'Routing rules', children: <RoutingRulesTab api={api} /> },
                ]}
            />
            <TicketDetailDrawer
                key={selected ? selected.id : 'none'}
                open={!!selected}
                ticket={selected}
                staff
                api={api}
                myUserId={myUserId}
                staffList={staff.status === 'ready' ? staff.data : undefined}
                projectLabel={selected && selected.projectId ? projectLabels[selected.projectId] : undefined}
                onClose={() => setSelectedId(null)}
                onTicketChanged={(t) => setOverrides((prev) => ({ ...prev, [t.id]: { ...(prev[t.id] || pageRows.find((r) => r.id === t.id) || {}), ...t } }))}
                onActivity={tickets.reload}
            />
        </PageShell>
    );
};

export default SupportConsolePage;
